import React, { useContext, useState, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { Column } from 'react-table';
import { ResponseError } from '@nintendo/forms';
import { useMsal } from '@azure/msal-react';
import { RoleUtils } from '@galaxy/lorule-react-commons';
import DataGrid from '../../common/datatables/DataGrid';
import AttachmentModal from '../modal/AttachmentModal';
import { ActionMemo } from '../../../api/actionMemoApiTypes';
import { FormType } from '../../common/formTypes';
import {
    formatDate,
    convertEmptyStringToDashes,
    sendToast,
    truncateString,
} from '../../../utils/displayUtils';
import './Attachments.css';
import DeleteConfirmationModal from '../../common/modal/DeleteConfirmationModal';
import { deleteAttachment, downloadAttachment } from '../../../api/attachmentApi';
import ConfigContext from '../../../contexts/ConfigContext';
import { AmStatusType, SYSTEM_EMPLOYEE_ID, DATE_TIME_FORMAT } from '../../../utils/Constants';

const Attachments = (props: { detail: ActionMemo; refresh: () => void }) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const [attachmentError, setAttachmentError] = useState('');

    const { detail } = props;

    const canDelete =
        (detail.currentUserIsCreatorOrProjectManager &&
            props.detail.amStatus === AmStatusType.Draft) ||
        (RoleUtils.checkRole(roles, ['ActionMemo.Admin']) &&
            (detail.amStatus === AmStatusType.Draft ||
                detail.amStatus === AmStatusType.Pending ||
                detail.amStatus === AmStatusType.Hold ||
                detail.amStatus === AmStatusType.Approved));

    const attachments = props.detail.attachments
        ? props.detail.attachments.map((attachment) => ({
              amId: detail.id,
              attachmentId: attachment.attachmentId,
              attachedBy:
                  attachment.attachedBy === SYSTEM_EMPLOYEE_ID
                      ? 'System'
                      : `${attachment.attachedByName} (${attachment.attachedByTitle})`,
              attachmentName: attachment.attachmentName,
              attachedOn: formatDate(attachment.attachedOn, DATE_TIME_FORMAT),
              description: attachment.description,
              mimeType: attachment.mimeType,
          }))
        : [];

    const columns = React.useMemo(
        () => [
            {
                Header: '',
                accessor: 'mimeType',
                Cell: (cellProps: { value: string }) =>
                    useMemo(() => {
                        let icon = <i className="fal fa-file-alt fa-2x" />;
                        if (cellProps.value === 'application/pdf') {
                            icon = <i className="fal fa-file-pdf fa-2x attachment-type pdf" />;
                        } else if (
                            cellProps.value === 'application/msword' ||
                            cellProps.value ===
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                        ) {
                            icon = <i className="fal fa-file-word fa-2x attachment-type word" />;
                        } else if (
                            cellProps.value === 'application/vnd.ms-powerpoint' ||
                            cellProps.value ===
                                'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                        ) {
                            icon = (
                                <i className="fal fa-file-powerpoint fa-2x attachment-type powerpoint" />
                            );
                        } else if (
                            cellProps.value === 'application/vnd.ms-excel' ||
                            cellProps.value ===
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        ) {
                            icon = <i className="fal fa-file-excel fa-2x attachment-type excel" />;
                        }
                        return <span>{icon}</span>;
                    }, []),
                disableSortBy: true,
                xs: 2,
                sm: 3,
                xl: 1,
            },
            {
                Header: 'Name',
                accessor: 'attachmentName',
                Cell: (cellProps: {
                    row: {
                        original: { amId: number; attachmentId: number; attachmentName: string };
                    };
                }) =>
                    useMemo(
                        () => (
                            <Button
                                variant="link"
                                className="text-left"
                                onClick={() => {
                                    downloadAttachment(
                                        config,
                                        cellProps.row.original.amId,
                                        cellProps.row.original.attachmentId,
                                        setAttachmentError,
                                        !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
                                    );
                                }}
                            >
                                {cellProps.row.original.attachmentName}
                            </Button>
                        ),
                        [],
                    ),
                xs: 10,
                sm: 9,
                xl: canDelete ? 3 : 4,
            },
            {
                Header: 'Description',
                accessor: 'description',
                Cell: (cellProps: { value: string }) => convertEmptyStringToDashes(cellProps.value),
                xs: 12,
                sm: 12,
                xl: 3,
            },
            {
                Header: 'Uploaded By',
                accessor: 'attachedBy',
                xs: 12,
                sm: 12,
                xl: 2,
            },
            {
                Header: 'Uploaded On',
                accessor: 'attachedOn',
                xs: 12,
                sm: 12,
                xl: 2,
            },
            ...(canDelete
                ? [
                      {
                          Header: '',
                          accessor: 'attachmentId',
                          Cell: (cellProps: {
                              row: {
                                  original: {
                                      amId: number;
                                      attachmentId: number;
                                      attachmentName: string;
                                  };
                              };
                          }) =>
                              useMemo(
                                  () => (
                                      <DeleteConfirmationModal
                                          title="Delete Attachment"
                                          onConfirm={async () => {
                                              await deleteAttachment(
                                                  config,
                                                  cellProps.row.original.amId,
                                                  cellProps.row.original.attachmentId,
                                                  !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
                                              );
                                              sendToast(
                                                  <>
                                                      Deleted Attachment:{' '}
                                                      <strong>
                                                          {truncateString(
                                                              cellProps.row.original.attachmentName,
                                                          )}
                                                      </strong>
                                                  </>,
                                              );
                                              props.refresh();
                                          }}
                                          body="Please confirm that you would like to delete this attachment. "
                                          toggleButton={
                                              <Button variant="link" className="text-danger">
                                                  <i className="far fa-trash-alt text-danger" />{' '}
                                                  Delete
                                              </Button>
                                          }
                                          confirmButtonText="Confirm Delete"
                                      />
                                  ),
                                  [],
                              ),
                          disableSortBy: true,
                          xs: 6,
                          sm: 6,
                          xl: 1,
                          customClass: 'mobile-hide',
                      },
                  ]
                : []),
        ],
        [],
    );

    return (
        <>
            <ResponseError errorMessage={attachmentError} />

            {detail.id &&
                (detail.amStatus === AmStatusType.Draft ||
                    detail.amStatus === AmStatusType.Pending ||
                    detail.amStatus === AmStatusType.Hold ||
                    detail.amStatus === AmStatusType.Approved) && (
                    <div className="col-sm-12 my-2 py-2 text-right mobile-hide border-bottom">
                        <AttachmentModal
                            title="Upload Attachment"
                            toggleButton={
                                <Button variant="outline-primary" className="ml-auto" size="sm">
                                    <i className="far fa-plus" /> Upload
                                </Button>
                            }
                            formType={FormType.CREATE}
                            amId={detail.id}
                            amNumber={detail.amNumber}
                            supNumber={detail.supNumber}
                            amStatus={detail.amStatus}
                            attachments={detail.attachments}
                            onSubmit={props.refresh}
                        />
                    </div>
                )}
            <div className="react-table px-2">
                <DataGrid
                    columns={columns as Column<Object>[]}
                    data={attachments}
                    noGlobalFilter
                    noPagination
                    initialState={{ pageSize: 1000 }}
                    mobileShowList
                />
            </div>
        </>
    );
};

export default Attachments;
